// frontend/src/pages/GarageIntroPage.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaCar, FaRobot, FaHistory, FaShieldAlt, FaCheckCircle, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext'; // <--- IMPORT AUTH

function GarageIntroPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth(); // <--- CHECK AUTH STATUS

  // Determine destination based on auth status
  const ctaLink = isAuthenticated ? "/account?tab=garage" : "/register";
  const ctaText = isAuthenticated ? "Go to My Garage" : "Create My Garage for Free";

  return (
    <div className="bg-white">
      
      {/* --- HERO SECTION --- */}
      <div className="relative bg-gray-900 text-white py-20 px-4 overflow-hidden min-h-[80vh] flex flex-col justify-center">
        
        {/* Background Image Layer */}
        <div className="absolute inset-0 z-0">
            <img 
                src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?q=80&w=1920&auto=format&fit=crop" 
                alt="Garage Hero" 
                className="w-full h-full object-cover opacity-50"
            />
            {/* Gradient Overlay for Text Contrast */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-gray-900"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          
          {/* Back Button */}
          <button 
            onClick={() => navigate(-1)}
            className="absolute top-[-40px] left-0 flex items-center text-gray-300 hover:text-white transition-colors font-bold text-sm uppercase tracking-wide"
          >
            <FaArrowLeft className="mr-2" /> Go Back
          </button>

          <div className="text-center mt-10">
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight drop-shadow-xl">
              Give Your Car a <span className="text-blue-400">Digital Brain</span>.
            </h1>
            
            {/* High Visibility Text Box */}
            <div className="max-w-3xl mx-auto mb-10 bg-black/40 backdrop-blur-sm p-6 rounded-2xl border border-white/10 shadow-2xl">
                <p className="text-xl md:text-2xl text-white font-medium leading-relaxed drop-shadow-md">
                  "My Garage" isn't just a list. It's an AI-powered health monitor that tracks your vehicle's history, predicts maintenance, and ensures you never buy the wrong part again.
                </p>
            </div>

            <Link 
              to={ctaLink} 
              className="inline-flex items-center bg-primary hover:bg-blue-600 text-white font-bold py-4 px-10 rounded-full text-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/50 border-2 border-transparent hover:border-blue-300"
            >
              {ctaText} <FaArrowRight className="ml-2" />
            </Link>
            {!isAuthenticated && (
                <p className="mt-4 text-sm text-gray-400 font-medium">Takes less than 2 minutes. No credit card required.</p>
            )}
          </div>
        </div>
      </div>

      {/* --- THE AI ADVANTAGE SECTION --- */}
      <div className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Why Register Your Car?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              When you add your vehicle to My Garage, our AI Mechanic gains "Context Awareness." It stops guessing and starts knowing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-3xl mb-6 mx-auto">
                <FaRobot />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">Personalized AI Advice</h3>
              <p className="text-gray-600 text-center">
                The AI knows your mileage and model. Ask <em>"Is my car safe for a long trip?"</em> and it analyzes your specific service history to give a data-driven answer.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-3xl mb-6 mx-auto">
                <FaCheckCircle />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">100% Part Compatibility</h3>
              <p className="text-gray-600 text-center">
                Stop guessing. Our "Smart Gate" technology checks every part against your Garage's VIN and specs. We tell you if it fits <strong>before</strong> you pay.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-3xl mb-6 mx-auto">
                <FaHistory />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">Digital Service Log</h3>
              <p className="text-gray-600 text-center">
                Every repair done by our partners is automatically logged. A complete, verified service history increases your car's resale value significantly.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* --- HOW IT WORKS --- */}
      <div className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-gray-800 mb-8">How It Works</h2>
              <div className="space-y-8">
                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl shadow-lg">1</div>
                  <div>
                    <h4 className="font-bold text-xl text-gray-800 mb-1">Create an Account</h4>
                    <p className="text-gray-600">Sign up in seconds. It's your secure portal.</p>
                  </div>
                </div>
                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl shadow-lg">2</div>
                  <div>
                    <h4 className="font-bold text-xl text-gray-800 mb-1">Add Your Vehicle</h4>
                    <p className="text-gray-600">Enter your Make, Model, Year, and VIN. You can add multiple cars.</p>
                  </div>
                </div>
                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xl shadow-lg">3</div>
                  <div>
                    <h4 className="font-bold text-xl text-gray-800 mb-1">Let the AI Take Over</h4>
                    <p className="text-gray-600">The AI immediately starts monitoring compatibility and service intervals.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-1/2 bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-inner">
                <div className="bg-white p-5 rounded-xl shadow-sm mb-6 border border-gray-100">
                    <div className="flex items-center gap-4 mb-3">
                        <div className="bg-gray-100 p-3 rounded-full">
                            <FaCar className="text-gray-500 text-xl" />
                        </div>
                        <div>
                            <span className="font-bold text-gray-800 block text-lg">2015 Toyota Camry</span>
                            <span className="text-xs text-gray-500">VIN: 4T1BF1FK...</span>
                        </div>
                    </div>
                    <div className="text-sm text-green-600 flex items-center gap-2 font-bold bg-green-50 p-2 rounded-lg">
                        <FaShieldAlt /> AI Monitoring Active
                    </div>
                </div>
                <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 relative">
                    <div className="absolute -top-3 -left-3 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                        AI Insight
                    </div>
                    <p className="text-sm text-blue-800 font-bold mb-2 flex items-center gap-2">
                        <FaRobot /> Mechanic says:
                    </p>
                    <p className="text-gray-700 italic leading-relaxed">
                        "Based on your last service 3 months ago and current mileage (120,000km), I recommend checking your brake pads before your trip to Abuja. I found 3 compatible OEM pads in our store that fit your VIN."
                    </p>
                </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- CTA FOOTER --- */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-20 px-4 text-center text-white">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Ready to upgrade your car ownership experience?</h2>
        <Link 
            to={ctaLink} 
            className="inline-block bg-white text-blue-700 font-bold py-4 px-12 rounded-full text-lg hover:bg-gray-100 transition-all shadow-xl transform hover:-translate-y-1"
        >
            {ctaText}
        </Link>
      </div>

    </div>
  );
}

export default GarageIntroPage;